import {useCallback, useEffect, useRef} from 'react';
import {useIdleAutoSave, type UseIdleAutoSaveResult} from '@/hooks/useIdleAutoSave';
import type {WpPersistOptions} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpPersistOptions';

export interface UseWpIdleAutoSaveOptions {
  /** Dialog open flag */
  open: boolean;
  /** Extra gate (e.g. initialized, not in Adjustment mode) */
  enabled?: boolean;
  /** Persist with closeOnSuccess/silent options */
  persist: (opts?: WpPersistOptions) => Promise<boolean | void>;
  /** Element that receives input/change (DialogContent). Falls back to document. */
  contentEl?: HTMLElement | null;
}

/**
 * Idle auto-save for Working Paper dialogs.
 * Shows Unsaved as soon as the dialog opens; marks dirty on input/change;
 * persists about once per minute while dirty (does not wait for mouse idle).
 *
 * `markDirty` itself defers UI setState, so this listener may call it
 * synchronously without swallowing controlled-input keystrokes.
 */
export function useWpIdleAutoSave({
  open,
  enabled = true,
  persist,
  contentEl,
}: UseWpIdleAutoSaveOptions): UseIdleAutoSaveResult {
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const onSave = useCallback(async () => {
    return persistRef.current({closeOnSuccess: false, silent: true});
  }, []);

  const idle = useIdleAutoSave({
    enabled: open && enabled,
    onSave,
    target: contentEl ?? undefined,
  });

  const markDirty = idle.markDirty;
  const markClean = idle.markClean;
  const armPendingDisplay = idle.armPendingDisplay;

  // Show Unsaved immediately when dialog opens (do NOT wait for enabled / first keypress).
  // Only reset when dialog closes — gating on `enabled` was clearing status back to idle
  // while bankInitReady/initialized was still false.
  useEffect(() => {
    if (!open) {
      markClean();
      return;
    }
    armPendingDisplay();
  }, [open, armPendingDisplay, markClean]);

  useEffect(() => {
    if (!open || !enabled) return;
    const el: HTMLElement | Document = contentEl ?? document;
    const onEdit = () => markDirty();
    // Bubble phase so field onChange/draft updates run before dirty→re-render
    el.addEventListener('input', onEdit, false);
    el.addEventListener('change', onEdit, false);
    return () => {
      el.removeEventListener('input', onEdit, false);
      el.removeEventListener('change', onEdit, false);
    };
  }, [open, enabled, contentEl, markDirty]);

  // While dialog is open, never surface raw `idle` (looks like Unsaved is missing until first key).
  // Keep real saving/saved/error/pending; map idle → pending for display.
  const status =
    open && idle.status === 'idle' ? 'pending' : idle.status;

  return {
    ...idle,
    status,
  };
}
